/**
 * Binary Format Preview Component
 *
 * Test panel for binary format definitions.
 * Allows entering sample data and viewing parsed results.
 */

import React from 'react';
import {
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Copy,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useBinaryFormatStore } from '@/stores/binary-formats/binary-format-store';

export const BinaryFormatPreview: React.FC = () => {
  const {
    currentFormat,
    sampleData,
    setSampleData,
    previewResult,
    isPreviewLoading,
    error,
    runPreview,
    clearPreview,
  } = useBinaryFormatStore();

  const handleRunPreview = () => {
    runPreview();
  };

  const handleCopyResult = () => {
    if (previewResult?.result?.data) {
      navigator.clipboard.writeText(
        JSON.stringify(previewResult.result.data, null, 2)
      );
    }
  };

  const getPlaceholder = (): string => {
    switch (currentFormat?.inputFormat) {
      case 'base64':
        return 'Enter base64-encoded data, e.g., SGVsbG8gV29ybGQ=';
      case 'hex':
        return 'Enter hex bytes, e.g., 48 65 6C 6C 6F or 48656C6C6F';
      case 'binary':
        return 'Enter binary string, e.g., 01001000 01100101';
      case 'raw':
        return 'Enter raw byte values';
      default:
        return 'Enter sample data...';
    }
  };

  if (!currentFormat) {
    return null;
  }

  return (
    <div className="h-[50vh] flex flex-col">
      <div className="grid grid-cols-2 gap-4 flex-1 min-h-0">
        {/* Left: Input Section */}
        <div className="flex flex-col space-y-3">
          <div className="space-y-2 flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between">
              <Label htmlFor="sample-data">Sample Data</Label>
              <Badge variant="outline" className="text-xs font-mono">
                {currentFormat.inputFormat.toUpperCase()}
              </Badge>
            </div>
            <Textarea
              id="sample-data"
              value={sampleData}
              onChange={(e) => setSampleData(e.target.value)}
              placeholder={getPlaceholder()}
              className="flex-1 font-mono text-sm resize-none"
            />
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={handleRunPreview}
              disabled={!sampleData || isPreviewLoading || !currentFormat.fields?.length}
              className="flex-1"
            >
              {isPreviewLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Parsing...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Test Parse
                </>
              )}
            </Button>
            {previewResult && (
              <Button variant="outline" size="icon" onClick={handleCopyResult}>
                <Copy className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Validation Messages */}
          {!currentFormat.fields?.length && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Add at least one field definition before testing.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        {/* Right: Result Section */}
        <div className="flex flex-col space-y-3">
          {previewResult ? (
            <>
              {/* Status Header */}
              <Card className="shrink-0">
                <CardHeader className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {previewResult.result.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-destructive" />
                      )}
                      <CardTitle className="text-base">
                        {previewResult.result.success ? 'Parse Successful' : 'Parse Failed'}
                      </CardTitle>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {previewResult.result.executionTimeMs.toFixed(2)}ms
                      </div>
                      <Badge variant="secondary">
                        {previewResult.byteCount} bytes
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Parsed Data */}
              <Card className="flex-1 min-h-0 flex flex-col">
                <CardHeader className="py-2 px-4 shrink-0">
                  <CardTitle className="text-sm">Parsed Values</CardTitle>
                </CardHeader>
                <CardContent className="p-0 flex-1 min-h-0">
                  <ScrollArea className="h-full">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-1/3">Field</TableHead>
                          <TableHead>Value</TableHead>
                          <TableHead className="w-24 text-right">Type</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(previewResult.result.data).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell className="font-medium">{key}</TableCell>
                            <TableCell className="font-mono text-sm">
                              {formatValue(value)}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground text-xs">
                              {typeof value}
                            </TableCell>
                          </TableRow>
                        ))}
                        {Object.keys(previewResult.result.data).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground">
                              No data extracted
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Errors & Warnings */}
              {(previewResult.result.errors.length > 0 ||
                previewResult.result.warnings.length > 0) && (
                <Card className="shrink-0">
                  <CardContent className="py-2 px-4 space-y-2">
                    {previewResult.result.errors.map((err, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-destructive">
                        <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        <div>
                          <span className="font-medium">{err.field}:</span> {err.message}
                        </div>
                      </div>
                    ))}
                    {previewResult.result.warnings.map((warn, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-yellow-600">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <div>{warn}</div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Hex Dump (collapsible) */}
              <Card className="shrink-0">
                <CardHeader className="py-2 px-4">
                  <CardTitle className="text-sm">Hex Dump</CardTitle>
                </CardHeader>
                <CardContent className="py-2 px-4">
                  <pre className="text-xs font-mono bg-muted p-2 rounded overflow-x-auto max-h-20">
                    {previewResult.hexDump || formatHexDump(previewResult.decimalDump)}
                  </pre>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <Play className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Enter sample data and click "Test Parse"</p>
                <p className="text-xs mt-1">to see the parsed output</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ================================
// Utility Functions
// ================================

/**
 * Format a value for display
 */
function formatValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  if (typeof value === 'number') {
    // Show both decimal and hex for numbers
    if (Number.isInteger(value) && value >= 0 && value <= 0xffff) {
      return `${value} (0x${value.toString(16).toUpperCase()})`;
    }
    // Round floats for display
    return Number.isInteger(value) ? String(value) : value.toFixed(4);
  }
  return String(value);
}

/**
 * Format decimal bytes as hex dump
 */
function formatHexDump(bytes: number[]): string {
  if (!bytes || bytes.length === 0) return 'No data';

  const lines: string[] = [];
  for (let i = 0; i < bytes.length; i += 16) {
    const chunk = bytes.slice(i, i + 16);
    const hex = chunk.map((b) => b.toString(16).padStart(2, '0').toUpperCase()).join(' ');
    const ascii = chunk
      .map((b) => (b >= 32 && b < 127 ? String.fromCharCode(b) : '.'))
      .join('');
    lines.push(
      `${i.toString(16).padStart(4, '0').toUpperCase()}  ${hex.padEnd(48)}  ${ascii}`
    );
  }
  return lines.join('\n');
}

export default BinaryFormatPreview;
